import debounce from 'debounce';
import { Decorator, FormApi } from 'final-form';

import actions from 'modules/form/actions';
import FormKeys from 'modules/form/types/FormKeys';

import type { AppDispatch } from '../../../App/Store';

export const upsertFormDebounce = debounce<
  <T>(key: FormKeys, values: T, dispatch: AppDispatch) => void>((key, values, dispatch) => {
  dispatch(actions.upsertForm({ key, values }));
}, 500);

const upsertFormManyDebounce = debounce<<T>(cache: Array<{ key: FormKeys, values: T }>, dispatch: AppDispatch) => void>(
  (cache, dispatch) => {
    dispatch(actions.upsertFormMany(cache));
  }, 500);

/**
 * @description Upserts a single `key` into the store.
 * It upserts the `key` with its corresponding `values`.
 */
function upsertFormSingle<T>(key: FormKeys, values: T, dispatch: AppDispatch, shouldDebounce?: boolean) {
  if (shouldDebounce) {
    return upsertFormDebounce(key, values, dispatch);
  }

  return dispatch(actions.upsertForm({ key, values }));
}

/**
 * @description Upserts many `keys` into the store.
 * It upserts each `key` with its corresponding values (`values[key]`).
 */
function upsertFormMany<T>(keys: Array<FormKeys>, values: T, dispatch: AppDispatch, shouldDebounce?: boolean) {
  const newCache = keys.map((key) => ({
    key,
    values: values[key as keyof T],
  }));

  if (shouldDebounce) {
    return upsertFormManyDebounce(newCache, dispatch);
  }

  return dispatch(actions.upsertFormMany(newCache));
}

/**
 * @description Upserts the key(s) into the store.
 * - If there is only one key, the whole `values` object is upsert
 * - If there are multiple keys, we upsert each `key` with its corresponding values (`values[key]`).
 */
function upsertFormKeys<T>(keys: Array<FormKeys>, values: T, dispatch: AppDispatch, shouldDebounce: boolean) {
  const hasManyKeys = keys.length > 1;
  if (hasManyKeys) {
    return upsertFormMany(keys, values, dispatch, shouldDebounce);
  }

  return upsertFormSingle(keys[0], values, dispatch, shouldDebounce);
}

function getDecorator<T>(keys: Array<FormKeys>, dispatch: AppDispatch): Decorator<T> {
  return (form: FormApi<T>) => form.subscribe(({ values, submitting }) => {
    if (submitting) {
      // Do not debounce when submitting
      upsertFormKeys(keys, values, dispatch, false);
      // We don't use `pristine` here because we have to record when the user goes back to the original value
      // Otherwise there is a merge issue between the non pristine value and the original, reverted, one.
    } else if (form.getState().touched && values) {
      upsertFormKeys(keys, values, dispatch, true);
    }
  }, { values: true, submitting: true });
}

export default getDecorator;
